import React from 'react'
import { XStack, YStack } from 'tamagui'
import { Trans, useLingui } from '@lingui/react/macro'

import { Card } from '@newComponents/Card'
import { Icon } from '@newComponents/Icon'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocScreenshot } from '../shared/DocScreenshot'

const Overview = () => {
  const { t } = useLingui()

  return (
    <>
      <Card gap='$4'>
        <Paragraph size='$5'>
          <Trans>
            Your account is your identity on Guild. It connects you to the{' '}
            <Link href='/docs/guilds/overview'>Guilds</Link> you belong to, the{' '}
            <Link href='/docs/events/overview'>Events</Link> you attend, and the{' '}
            <Link href='/docs/presentations/overview'>Presentations</Link> you
            give. From your account, you can manage your public profile, control
            notification preferences, sync your calendar, and configure security
            settings.
          </Trans>
        </Paragraph>
        <DocScreenshot
          src='docs/account/user-profile-page.png'
          alt='User profile page for Taz Singh showing profile photo, background photo, display name, bio, Guilds, Events attended, and Presentations given'
          caption={t`Your profile page is your public identity on Guild`}
        />
      </Card>

      <Card gap='$4'>
        <DocSection id='what-you-can-do' title={t`What You Can Do`}>
          <YStack gap='$3'>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='mail'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Sign up and sign in
                  </Paragraph>{' '}
                  — Enter your email, receive a verification code, and you are
                  in. You can also set a password if you prefer email and
                  password sign-in. See{' '}
                  <Link href='/docs/account/sign-in'>Sign Up & Sign In</Link>{' '}
                  for the full walkthrough.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='person'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Customize your profile
                  </Paragraph>{' '}
                  — Add a profile photo, background photo, display name, and
                  bio. Your profile page shows your Guilds, Events, and
                  Presentations in one place. See{' '}
                  <Link href='/docs/account/profile'>Your Profile</Link> for
                  details.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='person-add'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Join Guilds
                  </Paragraph>{' '}
                  — Discover and join communities that match your interests.
                  Some Guilds are open to everyone, while others require a
                  membership form or approval. See{' '}
                  <Link href='/docs/account/joining'>Joining a Guild</Link>.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='mail-inbox'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Manage notifications
                  </Paragraph>{' '}
                  — Control which email notifications you receive, from Event
                  reminders to Guild updates. See{' '}
                  <Link href='/docs/account/notifications'>
                    Notification Settings
                  </Link>
                  .
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='calendar'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Sync your calendar
                  </Paragraph>{' '}
                  — Connect Google Calendar to automatically add Events you RSVP
                  to. See{' '}
                  <Link href='/docs/account/calendar'>Calendar Sync</Link>.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='lock-closed'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Review security settings
                  </Paragraph>{' '}
                  — Manage your sessions, password, and connected services. See{' '}
                  <Link href='/docs/account/security'>
                    Authentication & Security
                  </Link>
                  .
                </Trans>
              </Paragraph>
            </XStack>
          </YStack>
        </DocSection>
      </Card>
    </>
  )
}

export default Overview
